import { useEffect, useRef, useCallback } from 'react';

/**
 * Intercepts browser back/forward navigation and tab close/refresh
 * when the form has unsaved changes. Uses window.confirm for the prompt.
 *
 * @param {Function} isDirtyFn - Returns true if the form has unsaved changes
 * @param {string} message - The confirmation message to display
 */
const useUnsavedChangesGuard = (isDirtyFn, message) => {
  const isDirtyFnRef = useRef(isDirtyFn);
  const skipNextPopStateRef = useRef(false);

  useEffect(() => {
    isDirtyFnRef.current = isDirtyFn;
  }, [isDirtyFn]);

  useEffect(() => {
    // Push a sentinel entry so the first browser-back pops this
    // instead of leaving the route (which would unmount the component).
    window.history.pushState({ unsavedGuard: true }, '');

    const handlePopState = () => {
      if (skipNextPopStateRef.current) {
        skipNextPopStateRef.current = false;
        return;
      }

      if (!isDirtyFnRef.current()) {
        // Not dirty — let the real back happen
        window.history.back();
        return;
      }

      // Re-push sentinel to stay on the page
      window.history.pushState({ unsavedGuard: true }, '');

      // eslint-disable-next-line no-alert
      if (window.confirm(message)) {
        skipNextPopStateRef.current = true;
        // Go back twice: once for our re-pushed sentinel, once for real navigation
        window.history.go(-2);
      }
    };

    const handleBeforeUnload = (e) => {
      if (isDirtyFnRef.current()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [message]);

  const guardNavigation = useCallback(
    (navigateFn) => {
      if (!isDirtyFnRef.current()) {
        navigateFn();
        return;
      }
      // eslint-disable-next-line no-alert
      if (window.confirm(message)) {
        navigateFn();
      }
    },
    [message],
  );

  return { guardNavigation };
};

export default useUnsavedChangesGuard;
